import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Context from '../Context'
import useInterval from '../utils/useInterval'
import { apiFetch, apiJson } from '../utils/api'
import { getSessionId } from '../utils/session'

/**
 * The pieces the two boards — the search board and the upcoming fixtures — have in common: the feed they
 * both read, the way a kick off is written, and the look of a table cell.
 */

export const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

export const cellSx = { padding: '6px 10px', fontSize: '13px', whiteSpace: 'nowrap' }

export const headSx = {
  ...cellSx,
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: '#9A9EA3',
  textAlign: 'left',
  position: 'sticky',
  top: 0,
  backgroundColor: 'white',
  zIndex: 1,
  // an inset shadow rather than a border: a collapsed table's borders do not travel with a stuck header
  boxShadow: 'inset 0 -2px 0 #E0E0E0'
}

// the same two tints the event screen uses for a price that has just moved
export const MOVE_TINT = {
  up: 'rgba(7, 176, 130, 0.22)',
  down: 'rgba(198, 30, 31, 0.20)'
}

export const MOVE_MS = 3000

/**
 * The comparison feed, flattened to one list of fixtures carrying their own sport and league. Both boards
 * want every sport at once rather than the league the menu is pointing at, so this asks for the lot and
 * lets the filters do the narrowing.
 */
export function useFeed(pollingInterval, query = null) {
  const { polledHost, username, setRequestCount, setLastResponseTime } = useContext(Context)

  const [ events, setEvents ] = useState(null)
  const [ error, setError ] = useState('')
  // which question these events answer: a screen that freezes its rows has to know when the answer it
  // froze has been replaced, and with the server filtering now, a new question means a different page
  const [ token, setToken ] = useState('')

  // a poll that started before a filter changed must not paint over what the newer one found
  const seq = useRef(0)

  const load = useCallback(async () => {
    const mine = ++seq.current
    try {
      const sessionId = getSessionId()
      const asked = new URLSearchParams({ sessionId, addAll: 'false', favOnly: 'false', minYield: '-1', bookmaker: 'Bovada' })
      // the screen's own filters go to the server: it holds the whole feed anyway, and a board that
      // shows two hundred games has no use for the other seventeen hundred crossing the wire
      for (const [key, value] of Object.entries(query || {})) {
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
          continue
        }
        asked.set(key, Array.isArray(value) ? value.join(',') : String(value))
      }
      const url = `/odds-comparison01?${asked}`
      const response = await apiFetch(polledHost, url)
      const data = await response.json()

      if (mine !== seq.current) {
        return
      }

      const flat = []
      for (const sportItem of data) {
        for (const leagueItem of sportItem.leagues || []) {
          for (const event of leagueItem.eventList || []) {
            flat.push({ ...event, sport: sportItem.sport, league: leagueItem.league })
          }
        }
      }

      setEvents(flat)
      setToken(asked.toString())
      setError('')
      setRequestCount((prev) => prev + 1)
      setLastResponseTime(Date.now())
    } catch (e) {
      // a poll that lost the race must not paint its failure over the answer that won
      if (mine === seq.current) {
        setError(e.message || 'Could not load the feed')
      }
    }
    // eslint-disable-next-line
  }, [polledHost, username, JSON.stringify(query || {})])

  useEffect(() => { load() }, [load])
  useInterval(() => { load() }, pollingInterval)

  return { events, error, token }
}

/**
 * The ladders for a list of fixtures, priced by one book, in one request. The comparison feed carries a
 * book's main line and nothing else; everything below and above it — and the team totals — live in the
 * market snapshot, which the server already holds and slices.
 *
 * Which fixtures are wanted only changes when the filters do, so the request is keyed on the list of
 * them rather than on the objects the feed rebuilds twice a second.
 */
export function useBoardPrices(fixtures, pollingInterval, book = 'Pinnacle') {
  const { marketHost } = useContext(Context)
  const [ games, setGames ] = useState(null)
  const [ error, setError ] = useState('')
  const [ moved, setMoved ] = useState(new Map())
  const previousPrices = useRef(new Map())
  const moveTimers = useRef({})

  const wanted = useRef([])
  wanted.current = (fixtures || []).map((one) => ({ home: one.home, away: one.away, koTime: one.koTime }))
  const wantedKey = (fixtures || []).map((one) => `${one.home}|${one.away}|${one.koTime}`).join('~')

  const load = useCallback(async () => {
    if (!wanted.current.length) {
      setGames(new Map())
      return
    }
    try {
      const data = await apiJson(marketHost, '/api/market/board', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: wanted.current, book })
      })
      setGames(new Map((data.games || []).map((game) => [game.key, game])))
      setError('')
    } catch (e) {
      setError(String(e && e.message ? e.message : e))
    }
    // eslint-disable-next-line
  }, [marketHost, book, wantedKey])

  useEffect(() => {
    load()
  }, [load])

  useInterval(() => {
    load()
  }, pollingInterval)

  useEffect(() => () => {
    Object.values(moveTimers.current).forEach(clearTimeout)
  }, [])

  /**
   * A price that changes silently looks exactly like a price that is stuck. Every poll is compared with
   * the one before it and anything that moved is tinted for three seconds — green when it lengthened,
   * red when it shortened — so a board is visibly alive without anyone having to watch one number.
   *
   * One timer per price, so a second move does not get cleared early by the first one's timer.
   */
  useEffect(() => {
    if (!games) {
      return
    }
    const current = new Map()
    games.forEach((game, key) => {
      for (const line of legsOf(game)) {
        for (const leg of line.legs) {
          current.set(`${key}|${leg.runnerKey}`, leg.price)
        }
      }
    })

    const changes = []
    current.forEach((price, key) => {
      const before = previousPrices.current.get(key)
      if (before !== undefined && before !== price) {
        // decimal throughout on this route, so the bigger number is the better price
        changes.push([key, price > before ? 'up' : 'down'])
      }
    })
    previousPrices.current = current

    if (!changes.length) {
      return
    }
    setMoved((prev) => {
      const next = new Map(prev)
      changes.forEach(([key, direction]) => next.set(key, direction))
      return next
    })
    changes.forEach(([key]) => {
      clearTimeout(moveTimers.current[key])
      moveTimers.current[key] = setTimeout(() => {
        delete moveTimers.current[key]
        setMoved((prev) => {
          const next = new Map(prev)
          next.delete(key)
          return next
        })
      }, MOVE_MS)
    })
  }, [games])

  const movedOf = (gameKey, runnerKey) => moved.get(`${gameKey}|${runnerKey}`) || null

  return { games, error, movedOf }
}

// every line a board game carries, whichever market it belongs to
const legsOf = (game) => [
  game.moneyline,
  ...(game.spreads || []),
  ...(game.totals || []),
  ...(game.homeTeamTotals || []),
  ...(game.awayTeamTotals || [])
].filter(Boolean)
